import { getDevicesList, getLastPW, getPtoList } from 'api';
import { memo, useEffect, useState } from 'react';
import {
  Device,
  PayWindow,
  PayWindowGroup,
  PTO,
} from 'types/appTypes';
import {
  PayWindowBox,
  SortPayWindowGroupAndReplaceClearedWindow,
} from './ridesPage';

import { Box, styled, Typography } from '@mui/material';
import ContactBox from 'components/contactBox';
import DeviceBox from 'components/deviceBox';
import OverviewHeader from 'components/overview/overviewHeader';
import Spinner from 'components/spinner';
import dayjs from 'dayjs';
import { loadFromLocalStorage } from 'localStorage';
import { GATEWAY } from 'localStorage/localStorageKeys';
import Lodash from 'lodash';
import { useTranslation } from 'react-i18next';
import { formatDateToDay } from 'utils/funcs/formatDate';
import { ContactWhiteBox } from './contacts';

interface DashboardState {
  contacts: PTO[];
  lastPW: PayWindowGroup[];
  devices: Device[];
}

const PAY_WINDOW_GROUP_INDEX = 0;
const PAY_WINDOW_INDEX = 0;

const BoxesWrapper = styled('div')(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: spacing(4),
  marginTop: spacing(5),
  marginBottom: spacing(10),
}));

const Dashboard = () => {
  const [data, setData] = useState<DashboardState>({
    contacts: [],
    lastPW: [],
    devices: [],
  });
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.dashboard',
  });

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setShowLoader(true);
    const responseData = await Promise.all([
      getPtoList(),
      getLastPW(),
      getDevicesList(),
    ]);
    setData({
      contacts: responseData[0] ?? [],
      lastPW: responseData[1]?.data ?? [],
      devices: responseData[2] ?? [],
    });
    setShowLoader(false);
  };

  const replacePayWindowWithClearedOne = (
    payWindowToReplaceWith: PayWindow,
    sortingFunction: SortPayWindowGroupAndReplaceClearedWindow,
  ) => {
    if (data.lastPW) {
      const newPayWindowGroups = sortingFunction(
        payWindowToReplaceWith,
        data.lastPW,
      );
      const newState = Lodash.cloneDeep(data);
      setData({ ...newState, lastPW: newPayWindowGroups });
    }
  };

  if (showLoader) {
    return <Spinner marginTop />;
  }

  const showGatewayAlert = data.devices.some(
    (device) => device.brand === 'CLOSE_LOOP',
  );

  return (
    <div>
      <GatewayAlert show={showGatewayAlert}></GatewayAlert>
      <OverviewHeader
        boldInfo={t('latestRide')}
        info={
          data.lastPW
            ? formatDateToDay(
                dayjs(
                  data?.lastPW[PAY_WINDOW_GROUP_INDEX]?.day,
                  'DD.MM.YYYY',
                ),
              )
            : ''
        }
        actions={{ label: t('allRides'), to: '/journeys' }}
      />
      {data?.lastPW?.map((payWindowGroup, index) => {
        if (index === PAY_WINDOW_GROUP_INDEX) {
          return payWindowGroup.payWindows.map((payWindow, index) => {
            if (index === PAY_WINDOW_INDEX)
              return (
                <PayWindowBox
                  clearPayWindowCallBack={
                    replacePayWindowWithClearedOne
                  }
                  key={index}
                  payWindow={payWindow}
                />
              );
            return null;
          });
        }

        return null;
      })}
      <OverviewHeader
        boldInfo={t('devices')}
        actions={{ label: t('allDevices'), to: '/devices' }}
      />
      <BoxesWrapper>
        {data.devices.map((device, index) => (
          <DeviceBox
            key={index}
            id={device.id}
            deviceNumber={device.pan}
            name={device.name}
            status={device.status}
            type={device.type}
            balance={device.balance ?? null}
            currency={device.currency ?? null}
          />
        ))}
      </BoxesWrapper>
      <OverviewHeader
        boldInfo={t('contacts')}
        actions={{ label: t('allContacts'), to: '/contacts' }}
      />
      <BoxesWrapper>
        {data?.contacts &&
          data?.contacts?.length > 0 &&
          data?.contacts.map((contact, index) => (
            <ContactWhiteBox key={index}>
              <ContactBox
                id={contact.id}
                name={contact.name}
                phone={contact.phone}
                email={contact.email}
                logo={contact.logo}
                key={index}
              />
            </ContactWhiteBox>
          ))}
      </BoxesWrapper>
    </div>
  );
};

const GatewayAlert = memo(function GatewayAlert({
  show,
}: {
  show: boolean;
}) {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.dashboard',
  });

  if (!show) {
    return null;
  }

  const gateway = loadFromLocalStorage({ key: GATEWAY });

  if (!gateway) {
    return null;
  }

  const gatewayAlert = t(`alert_${gateway}`, {
    returnObjects: true,
  }) as { title?: string; text: string };

  if (typeof gatewayAlert !== 'object') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        backgroundColor: 'common.white',
        mb: 6,
      }}
    >
      {gatewayAlert.title ? (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'background.lightGrey.dark',
            px: 3,
            py: 2,
          }}
        >
          <Typography
            variant="h5"
            color="secondary.light"
            fontWeight="normal"
          >
            {gatewayAlert.title}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ px: 3, py: 2, whiteSpace: 'pre-wrap' }}>
        {gatewayAlert.text}
      </Box>
    </Box>
  );
});

export default Dashboard;
